import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { authExtrasApi } from '../api/modules';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  function validatePassword(pw: string): string | null {
    if (pw.length < 10) return 'New password must be at least 10 characters';
    if (!/[a-z]/.test(pw)) return 'New password must contain a lowercase letter';
    if (!/[A-Z]/.test(pw)) return 'New password must contain an uppercase letter';
    if (!/[0-9]/.test(pw)) return 'New password must contain a digit';
    if (!/[^a-zA-Z0-9]/.test(pw)) return 'New password must contain a special character';
    if (pw === currentPw) return 'New password must differ from current password';
    return null;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (newPw !== confirmPw) {
      setError('New password and confirmation do not match');
      return;
    }
    const v = validatePassword(newPw);
    if (v) { setError(v); return; }

    setBusy(true);
    try {
      await authExtrasApi.changePassword(currentPw, newPw);
      setSuccess(true);
      // Force re-login since the API revokes the current session
      setTimeout(async () => {
        await signOut();
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setError(e?.response?.data?.error?.message ?? 'Password change failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center paper-bg p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8740C" strokeWidth="2">
                <path d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <h1 className="serif-display text-2xl text-navy-800 font-medium">Change Password</h1>
          {user?.must_change_password ? (
            <p className="text-sm text-saffron-700 mt-1">First login — please set a new password to continue.</p>
          ) : (
            <p className="text-sm text-navy-800/60 mt-1">Choose a strong password. All other sessions will be revoked.</p>
          )}
        </div>

        {success ? (
          <div className="bg-green-50 border border-green-300 rounded-sm p-6 text-center">
            <div className="text-base font-medium text-green-900 mb-2">Password changed</div>
            <p className="text-sm text-green-800">All sessions revoked. Redirecting to login…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="bg-white border border-cream-200 rounded-sm p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Current password</label>
              <input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                autoFocus
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm font-numeric"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">New password</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm font-numeric"
              />
              <div className="text-[11px] text-navy-800/50 mt-1 leading-relaxed">
                10+ chars · 1 uppercase, 1 lowercase, 1 digit, 1 special character
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Confirm new password</label>
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm font-numeric"
              />
            </div>

            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2.5">{error}</div>
            )}

            <button
              type="submit"
              disabled={busy || !currentPw || !newPw || !confirmPw}
              className="w-full bg-navy-800 hover:bg-navy-700 disabled:opacity-50 text-white text-sm py-2.5 rounded-sm transition"
            >
              {busy ? 'Changing…' : 'Change password'}
            </button>

            {!user?.must_change_password && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full text-xs text-navy-800/60 hover:text-navy-800"
              >
                Cancel
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
